/**
 * Suite model access law (BookForge · TextForge · Atherra):
 *
 * | Who     | Access                                              |
 * |---------|-----------------------------------------------------|
 * | Free    | Open-weight / non-CF ~7B class                      |
 * | Credits | Non-CF mid-size ~14–72B pay-as-you-go               |
 * | Member  | Cloudflare API + full large catalog + suite unlock  |
 */

import type { ModelAccessResult, ModelGate } from "./types.js";

export let SUITE_MEMBER_PRICE_USD = 2.99;

export function syncMemberPrice(price: number): void {
  if (price >= 0.5) SUITE_MEMBER_PRICE_USD = price;
}

export const FREE_MAX_PARAMS_B = 9;
export const CREDITS_MAX_PARAMS_B = 85;
export const CREDITS_LAUNCH_MIN_USD = 0.05;

export const FREE_STANDARD_BANDS = [{ min: 0, max: 9, label: "~7–8B" }] as const;

export function freeModelBandLabel(): string {
  return FREE_STANDARD_BANDS.map((b) => b.label).join(" · ");
}

export function creditsBandLabel(): string {
  return "~14–72B open-weight with credits";
}

export interface CloudflareHints {
  cloudflare?: boolean;
  provider?: string | null;
  tags?: string[] | null;
  offerId?: string | null;
  hardwareId?: string | null;
  modelId?: string | null;
  inferenceCatalog?: boolean;
}

export function isCloudflareModel(h: CloudflareHints = {}): boolean {
  if (h.inferenceCatalog) return true;
  if (h.cloudflare) return true;
  const prov = String(h.provider || "").toLowerCase();
  if (prov.includes("cloudflare") || prov === "cf") return true;
  const tags = (h.tags || []).map((t) => String(t).toLowerCase());
  if (tags.some((t) => t.includes("cloudflare") || t === "cf" || t === "one-bill")) {
    return true;
  }
  const oid = String(h.offerId || "");
  if (oid.startsWith("cf_inf_") || oid.startsWith("cf_")) return true;
  if (h.hardwareId === "forge-cf") return true;
  const mid = String(h.modelId || "").toLowerCase();
  if (mid.startsWith("@cf/") || mid.includes("workers-ai")) return true;
  return false;
}

export function gateForParamsB(paramsB: number | null | undefined): ModelGate {
  const p = Number(paramsB) || 0;
  if (p > 0 && p <= FREE_MAX_PARAMS_B) return "open";
  if (p > FREE_MAX_PARAMS_B && p <= CREDITS_MAX_PARAMS_B) return "credits";
  return "member";
}

export function canAccessModel(
  opts: {
    paramsB?: number | null;
    isMember: boolean;
    balanceUsd: number;
    memberPriceUsd?: number;
  } & CloudflareHints
): ModelAccessResult {
  if (opts.isMember) {
    return { allowed: true, gate: "member", badge: null, message: null };
  }

  const price = opts.memberPriceUsd ?? SUITE_MEMBER_PRICE_USD;

  if (isCloudflareModel(opts)) {
    return {
      allowed: false,
      gate: "member",
      badge: "Member",
      message: `Cloudflare models need Suite Member ($${price}/mo) — same unlock on BookForge, TextForge, and Atherra.`,
    };
  }

  const gate = gateForParamsB(opts.paramsB);

  if (gate === "open") {
    return { allowed: true, gate, badge: null, message: null };
  }
  if (gate === "credits") {
    if ((Number(opts.balanceUsd) || 0) >= CREDITS_LAUNCH_MIN_USD) {
      return { allowed: true, gate, badge: null, message: null };
    }
    return {
      allowed: false,
      gate,
      badge: "Credits",
      message: `Add credits (min $5) for mid-size open-weight models (${creditsBandLabel()}). Cloudflare + full catalog need Member $${price}/mo.`,
    };
  }
  return {
    allowed: false,
    gate: "member",
    badge: "Member",
    message: `Large models need Suite Member ($${price}/mo). Free: ${freeModelBandLabel()}; credits: ${creditsBandLabel()}.`,
  };
}

/** Cloudflare catalog → Member only (default). */
export function canAccessInferenceModel(opts: {
  paramsB?: number | null;
  modality?: string;
  isMember: boolean;
  balanceUsd?: number;
  memberPriceUsd?: number;
}): ModelAccessResult {
  if (opts.isMember) {
    return { allowed: true, gate: "member", badge: null, message: null };
  }
  const price = opts.memberPriceUsd ?? SUITE_MEMBER_PRICE_USD;
  return {
    allowed: false,
    gate: "member",
    badge: "Member",
    message: `Cloudflare models are Suite Member only ($${price}/mo) across BookForge · TextForge · Atherra.`,
  };
}

export function modelGateBadge(
  paramsB: number | null | undefined,
  opts: {
    isMember: boolean;
    balanceUsd: number;
    memberPriceUsd?: number;
  } & CloudflareHints
): "Member" | "Credits" | null {
  const access = canAccessModel({ paramsB, ...opts });
  if (access.allowed) return null;
  return access.badge;
}
